'use client';

import { useRef, useState, useEffect } from 'react';
import clsx from 'clsx';
import { Locale } from '@/i18n/config';
import { setUserLocale, getUserLocale } from '@/i18n/locale';
import styles from './LocaleSwitcher.module.scss';
import { useOutsideClick } from '@/hooks/useOutsideClick';
import Icon from '../Icon';

interface Language {
  value: string;
  label: string
}

type Props = {
  defaultLocale: Language;
  languages: Array<Language>;
};

export default function LocaleSwitcherSelect({
  defaultLocale,
  languages,
}: Props) {
  const [selected, setSelected] = useState<Language>(defaultLocale);
  const [isOpened, setIsOpened] = useState<boolean>(false);

  const wrapperRef = useRef<HTMLDivElement | null>(null);
  useOutsideClick(wrapperRef, () => setIsOpened(false));

  useEffect(() => {
    const handleStorageChange = async () => {
      const savedLocale = await getUserLocale();
      const found = languages.find((l) => l.value === savedLocale);
      if (found) setSelected(found);
    };

    handleStorageChange();

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [languages]);

  const onSelect = async (lang: Language) => {
    await setUserLocale(lang.value as Locale);
    window.location.reload();
    setIsOpened(false);
    setSelected(lang);
  };

  return (
    <div
      className={clsx(styles.wrapper, {
        [styles.opened]: isOpened,
      })}
      ref={wrapperRef}
    >
      <div className={styles.selector}>
        <button
          type='button'
          className={styles.selector__button}
          onClick={() => setIsOpened(!isOpened)}
        >
          <div className={clsx(styles.selector__selected)}>
            <span className={styles.selector__lang}>{selected.label}</span>
            <Icon
              name='keyboard_arrow_down'
              size={18}
              className={styles.arrow}
            />
          </div>
        </button>

        {isOpened
          && languages?.map(
            (language) => language.value !== selected?.value && (
            <button
              type='button'
              onClick={() => onSelect(language)}
              key={language.value}
            >
              <div
                className={clsx(
                  styles.selector__item,
                  styles.selector__option,
                )}
              >
                <span className={styles.selector__lang}>
                  {language.label}
                </span>
              </div>
            </button>
            ),
          )}
      </div>
    </div>
  );
}
